/**
 * Audit log (`/audit`) — Admin only.
 *
 * Reads from /api/audit-log with filters: entityType, action, date range,
 * user. Renders a chronological table; oldValue/newValue/context shown
 * inline when the row is small enough to fit them. Page size capped at 50
 * by default, navigable via Prev/Next.
 *
 * The audit log is append-only on the server (INSERT-only DB grant); the UI
 * provides no edit affordance for that reason.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, FileText, RotateCcw, Trash2, X } from 'lucide-react';
import { formatDateTime } from '@procurement/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { Card } from '@/components/ui/card';
import { DataTable, type ColumnDef } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterChipGroup } from '@/components/ui/filter-chip';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { Select } from '@/components/ui/select';
import { SortableHeader } from '@/components/ui/sortable-header';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, type TabDef } from '@/components/ui/tabs';
import { useCurrentUser } from '@/lib/auth';
import {
  useAuditList,
  type AuditAction,
  type AuditEntityType,
  type AuditEntry,
  type AuditSortKey,
  type SortDir,
} from '@/lib/users-audit-queries';
import {
  useDeletedEntities,
  useRestoreProject,
  useRestoreProjectItem,
  type DeletedItem,
  type DeletedProject,
} from '@/lib/deleted-queries';
import { canRestoreItem, restoreBlockedReason } from '@/lib/deleted-helpers';
import { parseAuditMetadata } from '@/lib/audit-metadata';
import { usePageTitle } from '@/lib/use-page-title';

const ENTITY_TYPES: AuditEntityType[] = [
  'PROJECT',
  'PROJECT_ITEM',
  'ORDER_BATCH',
  'PART_STAGE_DATE',
  'INSPECTION_EVENT',
  'BANK_GUARANTEE',
  'PARTS_APPROVAL_SUBMISSION',
  'PROJECT_DEPARTMENT',
  'PROJECT_DEPARTMENT_CONTACT',
  'SUPPLIER',
  'CLIENT',
  'DEPARTMENT',
  'USER',
];

const COMMON_ACTIONS: AuditAction[] = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'RESTORE',
  'APPROVE_PARTS',
  'REJECT_PARTS',
  'CANCEL_BATCH',
  'CLOSE_PROJECT',
];

type AuditTabId = 'activity' | 'deleted';

const AUDIT_TABS: TabDef<AuditTabId>[] = [
  { id: 'activity', label: 'Activity' },
  { id: 'deleted', label: 'Deleted' },
];

const PAGE_SIZE = 50;

export function AuditLogPage() {
  usePageTitle('Audit log');
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const navigate = useNavigate();

  const [entityType, setEntityType] = useState<AuditEntityType | undefined>(undefined);
  const [action, setAction] = useState<AuditAction | undefined>(undefined);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<AuditTabId>('activity');
  // Patch 5 — sortable columns. Default matches the server's existing
  // order (occurredAt desc). Toggling the same column flips direction;
  // picking a different column resets direction to desc.
  const [sortBy, setSortBy] = useState<AuditSortKey>('occurredAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const toggleSort = (key: AuditSortKey): void => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(1);
  };

  useEffect(() => {
    if (me.data && !isAdmin) void navigate({ to: '/', replace: true });
  }, [isAdmin, me.data, navigate]);

  const query = useAuditList({
    entityType,
    action,
    fromDate: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
    toDate: toDate ? `${toDate}T23:59:59.999Z` : undefined,
    page,
    pageSize: PAGE_SIZE,
    sortBy,
    sortDir,
  });

  if (!isAdmin) return null;

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onResetFilters = (): void => {
    setEntityType(undefined);
    setAction(undefined);
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const columns: ColumnDef<AuditEntry>[] = [
    {
      id: 'when',
      header: (
        <SortableHeader
          label="When"
          active={sortBy === 'occurredAt'}
          direction={sortDir}
          onToggle={() => toggleSort('occurredAt')}
        />
      ),
      width: '170px',
      cell: (r) => (
        <span className="text-xs text-secondary tabular-nums">{formatDateTime(r.occurredAt)}</span>
      ),
    },
    {
      id: 'who',
      header: (
        <SortableHeader
          label="Who"
          active={sortBy === 'userDisplayName'}
          direction={sortDir}
          onToggle={() => toggleSort('userDisplayName')}
        />
      ),
      width: '160px',
      cell: (r) => <span className="text-primary">{r.userDisplayName}</span>,
    },
    {
      id: 'action',
      header: (
        <SortableHeader
          label="Action"
          active={sortBy === 'action'}
          direction={sortDir}
          onToggle={() => toggleSort('action')}
        />
      ),
      width: '180px',
      cell: (r) => <ActionBadge action={r.action} />,
    },
    {
      id: 'entity',
      header: (
        <SortableHeader
          label="Entity"
          active={sortBy === 'entityType'}
          direction={sortDir}
          onToggle={() => toggleSort('entityType')}
        />
      ),
      width: '170px',
      cell: (r) => (
        <div className="text-xs text-secondary">
          <div className="font-mono">{r.entityType}</div>
          <div className="truncate font-mono text-[10px] text-tertiary">
            {r.entityId.slice(0, 8)}…
          </div>
        </div>
      ),
    },
    {
      id: 'detail',
      header: 'Detail',
      cell: (r) => <DetailCell entry={r} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Append-only history of every mutation. Visible to administrators only."
      />

      <Tabs<AuditTabId> tabs={AUDIT_TABS} activeId={tab} onChange={setTab} className="mb-6" />

      {tab === 'deleted' ? <DeletedTab /> : null}
      {tab === 'activity' ? (
        <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterChipGroup>
          <span className="text-xs text-tertiary">Entity:</span>
          <Select
            value={entityType ?? ''}
            onChange={(e) => {
              setEntityType((e.target.value as AuditEntityType) || undefined);
              setPage(1);
            }}
            className="h-7 w-[200px] text-xs"
          >
            <option value="">All entities</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
          <span className="ml-2 text-xs text-tertiary">Action:</span>
          <Select
            value={action ?? ''}
            onChange={(e) => {
              setAction((e.target.value as AuditAction) || undefined);
              setPage(1);
            }}
            className="h-7 w-[180px] text-xs"
          >
            <option value="">All actions</option>
            {COMMON_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        </FilterChipGroup>
        <FilterChipGroup>
          <span className="text-xs text-tertiary">From:</span>
          <DmyDateInput
            value={fromDate}
            onChange={(value) => {
              setFromDate(value);
              setPage(1);
            }}
            showValidationMessage={false}
            className="h-7 w-[140px] text-xs"
          />
          <span className="text-xs text-tertiary">To:</span>
          <DmyDateInput
            value={toDate}
            onChange={(value) => {
              setToDate(value);
              setPage(1);
            }}
            showValidationMessage={false}
            className="h-7 w-[140px] text-xs"
          />
        </FilterChipGroup>
        {entityType || action || fromDate || toDate ? (
          <button
            type="button"
            onClick={onResetFilters}
            className="inline-flex items-center gap-1 text-xs text-tertiary hover:text-primary"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        ) : null}
      </div>

      <Card bodyClassName="p-0">
        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-6 w-6 text-secondary" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-6 w-6" />}
            title="No matching entries"
            body={
              entityType || action || fromDate || toDate
                ? 'Try widening the date range or clearing some filters.'
                : 'No audit log entries recorded yet.'
            }
          />
        ) : (
          <DataTable<AuditEntry> columns={columns} rows={rows} rowKey={(r) => r.id} />
        )}
      </Card>

      {total > PAGE_SIZE ? (
        <div className="mt-3 flex items-center justify-between text-sm text-secondary">
          <span>
            Page {page} of {totalPages} · {total} entries
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface px-2 py-1 text-xs hover:bg-subtle disabled:opacity-50"
            >
              <ChevronLeft className="h-3 w-3" /> Prev
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface px-2 py-1 text-xs hover:bg-subtle disabled:opacity-50"
            >
              Next <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      ) : null}
        </>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ActionBadge({ action }: { action: AuditAction }) {
  const tone =
    action === 'CREATE' || action === 'RESTORE'
      ? 'success'
      : action.startsWith('DELETE') ||
          action.startsWith('REJECT') ||
          action.startsWith('CANCEL') ||
          action === 'CLOSE_PROJECT'
        ? 'danger'
        : action.startsWith('APPROVE') || action.startsWith('FINALIZE')
          ? 'info'
          : 'neutral';
  return <Badge tone={tone as 'success' | 'danger' | 'info' | 'neutral'}>{action}</Badge>;
}

function DetailCell({ entry }: { entry: AuditEntry }) {
  // Field update: show "field: old → new"
  if (entry.action === 'UPDATE' && entry.fieldName) {
    const oldVal = safeRender(entry.oldValueJson);
    const newVal = safeRender(entry.newValueJson);
    return (
      <div className="text-xs">
        <span className="font-mono text-secondary">{entry.fieldName}:</span>{' '}
        <span className="text-tertiary line-through">{oldVal}</span>
        <span className="mx-1 text-tertiary">→</span>
        <span className="text-primary">{newVal}</span>
      </div>
    );
  }

  // Lifecycle / context actions — read structured metadata. Patch E6
  // routes everything through `parseAuditMetadata` so cascade deletes,
  // rejection comments, and other contexts render consistently.
  const meta = parseAuditMetadata(entry.metadataJson);
  if (meta) {
    if (meta.kind === 'rejection') {
      return <div className="text-xs italic text-secondary">“{meta.comment}”</div>;
    }
    if (meta.kind === 'reason') {
      return <div className="text-xs text-secondary">{meta.label}</div>;
    }
    // generic
    return (
      <div className="text-xs text-secondary">
        {meta.entries.map((e, i) => (
          <span key={e.key}>
            {i > 0 ? ', ' : ''}
            <span className="font-mono text-secondary">{e.key}:</span> {e.value}
          </span>
        ))}
      </div>
    );
  }

  return <span className="text-xs text-tertiary">—</span>;
}

function formatAuditPrimitiveValue(value: string): string {
  const mapped =
    value === 'OUR_PREMISES'
      ? 'Firm’s premises'
      : value === 'CLIENT_PREMISES'
        ? 'Client premises'
        : value === 'THIRD_PARTY_PREMISES'
          ? 'Third party premises'
          : value;
  return mapped.length > 40 ? `${mapped.slice(0, 40)}…` : mapped;
}

function safeRender(json: string | null): string {
  if (json === null) return '—';
  try {
    const v = JSON.parse(json);
    if (v === null) return '—';
    if (typeof v === 'string') return formatAuditPrimitiveValue(v);
    return JSON.stringify(v);
  } catch {
    return formatAuditPrimitiveValue(json);
  }
}

// ---------------------------------------------------------------------------
// Deleted tab (D6)
// ---------------------------------------------------------------------------

/**
 * Shows currently soft-deleted projects and items, with a Restore button
 * per row (Admin-only — the audit page itself is already gated). The
 * Restore button is disabled when restoration would 400 (parent not
 * live); the tooltip explains which parent to restore first.
 */
function DeletedTab() {
  const query = useDeletedEntities();
  const projects = query.data?.projects ?? [];
  const items = query.data?.items ?? [];

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }
  if (query.error) {
    return (
      <Card>
        <p className="text-sm text-danger-fg">Failed to load. {query.error.message}</p>
      </Card>
    );
  }
  if (projects.length === 0 && items.length === 0) {
    return (
      <EmptyState
        icon={<Trash2 className="h-6 w-6" strokeWidth={1.5} />}
        title="Nothing soft-deleted"
        body="Soft-deleted projects and parts/sub-parts appear here so they can be restored. The bin is currently empty."
      />
    );
  }

  return (
    <div className="space-y-4">
      {projects.length > 0 ? (
        <Card title={`Projects (${projects.length})`}>
          <ul className="divide-y divide-border-default">
            {projects.map((p) => (
              <DeletedProjectRow key={p.id} project={p} />
            ))}
          </ul>
        </Card>
      ) : null}
      {items.length > 0 ? (
        <Card title={`Parts & sub-parts (${items.length})`}>
          <ul className="divide-y divide-border-default">
            {items.map((i) => (
              <DeletedItemRow key={i.id} item={i} />
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

function DeletedProjectRow({ project }: { project: DeletedProject }) {
  const restore = useRestoreProject();
  return (
    <li className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <ProjectNumber number={project.projectNumber} size="sm" />
          <span className="truncate text-sm text-primary">{project.projectTitle}</span>
        </div>
        <p className="text-xs text-tertiary">
          Deleted {formatDateTime(project.deletedAt)}
          {project.deletedByDisplayName ? ` by ${project.deletedByDisplayName}` : ''}
        </p>
        {restore.error ? (
          <p className="mt-1 text-xs text-danger-fg">{restore.error.message}</p>
        ) : null}
      </div>
      <Button
        variant="secondary"
        onClick={() =>
          restore.mutate({ projectId: project.id, rowVersion: project.rowVersion })
        }
        disabled={restore.isPending}
        loading={restore.isPending}
      >
        <RotateCcw className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
        Restore
      </Button>
    </li>
  );
}

function DeletedItemRow({ item }: { item: DeletedItem }) {
  const restore = useRestoreProjectItem();
  const blocked = !canRestoreItem(item);
  const blockedHint = restoreBlockedReason(item);

  return (
    <li className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <Badge tone="neutral">{item.itemType === 'PART' ? 'Part' : 'Sub-part'}</Badge>
          <span className="truncate text-sm text-primary">{item.name}</span>
        </div>
        <p className="flex items-center gap-1.5 text-xs text-tertiary">
          {item.parentPartName ? <span>↳ {item.parentPartName} · </span> : null}
          <ProjectNumber number={item.projectNumber} size="sm" />
          <span>— {item.projectTitle}</span>
        </p>
        <p className="text-xs text-tertiary">
          Deleted {formatDateTime(item.deletedAt)}
          {item.deletedByDisplayName ? ` by ${item.deletedByDisplayName}` : ''}
        </p>
        {blockedHint ? <p className="mt-1 text-xs text-warning-fg">{blockedHint}</p> : null}
        {restore.error ? (
          <p className="mt-1 text-xs text-danger-fg">{restore.error.message}</p>
        ) : null}
      </div>
      <Button
        variant="secondary"
        onClick={() =>
          restore.mutate({
            projectId: item.projectId,
            itemId: item.id,
            rowVersion: item.rowVersion,
          })
        }
        disabled={blocked || restore.isPending}
        loading={restore.isPending}
        title={blockedHint ?? undefined}
      >
        <RotateCcw className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
        Restore
      </Button>
    </li>
  );
}
